import React, { useState, useEffect } from "react";
import axios from "axios";
import "../styles/UpdatePrompt.css";
import { environment } from "../env/environment";
import logger from '../utils/logger';

const UpdatePrompt = () => {
  const [imagePrompt, setImagePrompt] = useState("");
  const [docPrompt, setDocPrompt] = useState("");
  const [loading, setLoading] = useState({ image: false, document: false });
  const [message, setMessage] = useState({ image: "", document: "" });
  const [fetchingPrompts, setFetchingPrompts] = useState(true);

  useEffect(() => {
    fetchCurrentPrompts();
  }, []);

  const fetchCurrentPrompts = async () => {
    try {
      const res = await axios.get(`${environment.baseUrl}/api/prompts/get`);
      if (res.data.success) {
        setImagePrompt(res.data.prompts.image || "");
        setDocPrompt(res.data.prompts.document || "");
      }
    } catch (error) {
      logger.error("Error fetching prompts:", error);
      setMessage({
        image: "⚠️ Could not load existing prompts",
        document: "⚠️ Could not load existing prompts"
      });
    } finally {
      setFetchingPrompts(false);
    }
  };

  const handleUpdate = async (type) => {
    // Set loading for specific button only
    setLoading((prev) => ({ ...prev, [type]: true }));
    setMessage((prev) => ({ ...prev, [type]: "" }));

    try {
      const promptText = type === "image" ? imagePrompt : docPrompt;

      if (!promptText.trim()) {
        setMessage((prev) => ({
          ...prev,
          [type]: "⚠️ Please enter a prompt before updating."
        }));
        setLoading((prev) => ({ ...prev, [type]: false }));
        return;
      }

      const res = await axios.post(`${environment.baseUrl}/api/prompts/update`, {
        type,
        prompt: promptText,
      });

      if (res.data.success) {
        setMessage((prev) => ({
          ...prev,
          [type]: `✅ ${type.toUpperCase()} prompt updated successfully!`
        }));
      } else {
        setMessage((prev) => ({
          ...prev,
          [type]: `❌ Failed to update ${type} prompt.`
        }));
      }
    } catch (error) {
      logger.error(error);
      setMessage((prev) => ({
        ...prev,
        [type]: `❌ Error updating prompt: ${error.response?.data?.error || error.message}`
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  if (fetchingPrompts) {
    return (
      <div className="update-container">
        <h2>🔄 Loading prompts...</h2>
      </div>
    );
  }

  return (
    <div className="update-container">
      <h2> Update Extraction Prompts</h2>

      {/* IMAGE PROMPT SECTION */}
      <div className="prompt-section">
        <label> Update Image Prompt</label>
        <textarea
          value={imagePrompt}
          onChange={(e) => setImagePrompt(e.target.value)}
          placeholder="Write image prompt here..."
          rows={10}
        ></textarea>
        <button
          onClick={() => handleUpdate("image")}
          disabled={loading.image}
          className={loading.image ? "loading" : ""}
        >
          {loading.image ? "⏳ Updating..." : "Update Image Prompt"}
        </button>
        {message.image && (
          <p className={`status-message ${message.image.includes("✅") ? "success" : "error"}`}>
            {message.image}
          </p>
        )}
      </div>

      {/* DOCUMENT PROMPT SECTION */}
      <div className="prompt-section">
        <label> Update Document Prompt (PDF, DOC, TXT)</label>
        <textarea
          value={docPrompt}
          onChange={(e) => setDocPrompt(e.target.value)}
          placeholder="Write document prompt here..."
          rows={10}
        ></textarea>
        <button
          onClick={() => handleUpdate("document")}
          disabled={loading.document}
          className={loading.document ? "loading" : ""}
        >
          {loading.document ? "⏳ Updating..." : "Update Document Prompt"}
        </button>
        {message.document && (
          <p className={`status-message ${message.document.includes("✅") ? "success" : "error"}`}>
            {message.document}
          </p>
        )}
      </div>
    </div>
  );
};

export default UpdatePrompt;